import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getEquipmentList,
  saveEquipmentRow,
  deleteEquipmentRow,
  type EquipmentRow,
  type EquipmentStatus,
} from '@/services/db/dsrEntry';

const EQUIPMENT_TYPES = [
  'Crane', 'Pump', 'Generator', 'Vehicle', 'Vessel', 'Tool', 'Other',
] as const;

const STATUS_OPTIONS: { value: EquipmentStatus; label: string; bg: string }[] = [
  { value: 'active', label: 'Active', bg: 'bg-mozaic-green' },
  { value: 'standby', label: 'Standby', bg: 'bg-mozaic-teal' },
  { value: 'down', label: 'Down', bg: 'bg-mozaic-orange' },
  { value: 'removed', label: 'Removed', bg: 'bg-muted-foreground' },
];

const entrySchema = z.object({
  equipment_name: z.string().min(1, 'Equipment name is required'),
  equipment_type: z.string().optional(),
  equipment_identifier: z.string().optional(),
  status: z.enum(['active', 'standby', 'down', 'removed']),
  hours_operated: z.string().optional(),
  operator_crew: z.string().optional(),
  notes: z.string().optional(),
});

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  equipment_name: '',
  equipment_type: '',
  equipment_identifier: '',
  status: 'active',
  hours_operated: '',
  operator_crew: '',
  notes: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function EquipmentForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: EquipmentRow }>('closed');

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_equipment', dsrId],
    queryFn: () => getEquipmentList(dsrId),
    staleTime: 30 * 1000,
  });

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_equipment', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this equipment entry?')) return;
    try {
      await deleteEquipmentRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_equipment', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete equipment');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  const activeCount = entries.filter((e) => e.status === 'active').length;
  const downCount = entries.filter((e) => e.status === 'down').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Equipment</h2>
        <p className="text-xs text-muted-foreground">
          Track equipment and assets active during the shift.
        </p>
      </div>

      {entries.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4">
              <Stat value={entries.length} label="Total" tone="teal" />
              <Stat value={activeCount} label="Active" />
              <Stat value={downCount} label="Down" />
            </div>
          </CardContent>
        </Card>
      )}

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No equipment entries yet. Add equipment below.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <EquipmentCard
          key={entry.id}
          entry={entry}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <EquipmentEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => setMode('adding')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add Equipment
        </Button>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Stat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone?: 'teal';
}) {
  return (
    <div className="text-center">
      <div
        className={`text-2xl font-bold ${
          tone === 'teal' ? 'text-mozaic-teal' : 'text-mozaic-navy'
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function EquipmentCard({
  entry,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: EquipmentRow;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const statusMeta = STATUS_OPTIONS.find((s) => s.value === entry.status) ?? STATUS_OPTIONS[0];
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-mozaic-navy">{entry.equipment_name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {entry.equipment_identifier && <span>ID: {entry.equipment_identifier}</span>}
              {entry.hours_operated != null && <span>{entry.hours_operated}h operated</span>}
              {entry.operator_crew && <span>Crew: {entry.operator_crew}</span>}
            </div>
            {entry.notes && (
              <p className="mt-1 truncate text-xs text-muted-foreground">{entry.notes}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            {entry.equipment_type && (
              <Badge className="bg-mozaic-navy text-white">{entry.equipment_type}</Badge>
            )}
            <Badge className={`${statusMeta.bg} text-white`}>{statusMeta.label}</Badge>
          </div>
        </div>
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EquipmentEntryForm({
  dsrId,
  tenantId,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  editing?: EquipmentRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          equipment_name: editing.equipment_name,
          equipment_type: editing.equipment_type ?? '',
          equipment_identifier: editing.equipment_identifier ?? '',
          status: editing.status,
          hours_operated:
            editing.hours_operated != null ? String(editing.hours_operated) : '',
          operator_crew: editing.operator_crew ?? '',
          notes: editing.notes ?? '',
        }
      : EMPTY,
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveEquipmentRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        equipmentName: values.equipment_name,
        equipmentType: values.equipment_type || null,
        equipmentIdentifier: values.equipment_identifier || null,
        status: values.status,
        hoursOperated: values.hours_operated ? parseFloat(values.hours_operated) : null,
        operatorCrew: values.operator_crew || null,
        notes: values.notes || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save equipment');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Equipment' : 'New Equipment'}</h3>

        <div className="space-y-2">
          <FieldLabel required>Equipment Name</FieldLabel>
          <Controller
            control={control}
            name="equipment_name"
            render={({ field }) => <Input {...field} placeholder="e.g. Main Crane #2" />}
          />
          {errors.equipment_name && (
            <p className="text-xs text-mozaic-red">{errors.equipment_name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <FieldLabel>Equipment Type</FieldLabel>
          <Controller
            control={control}
            name="equipment_type"
            render={({ field }) => (
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT_TYPES.map((type) => {
                  const selected = field.value === type;
                  return (
                    <button
                      type="button"
                      key={type}
                      onClick={() => field.onChange(selected ? '' : type)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                        selected
                          ? 'border-mozaic-navy bg-mozaic-navy text-white'
                          : 'border-input bg-background hover:bg-muted'
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>Asset ID / Tag Number</FieldLabel>
          <Controller
            control={control}
            name="equipment_identifier"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} placeholder="e.g. CR-002, SN-12345" />
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <div className="flex gap-2">
                {STATUS_OPTIONS.map((opt) => {
                  const selected = field.value === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => field.onChange(opt.value)}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                        selected
                          ? `border-transparent ${opt.bg} text-white`
                          : 'border-input bg-background hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel>Hours Operated</FieldLabel>
            <Controller
              control={control}
              name="hours_operated"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  placeholder="0.0"
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>Operator / Crew</FieldLabel>
            <Controller
              control={control}
              name="operator_crew"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} placeholder="e.g. Drilling Crew A" />
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>Notes</FieldLabel>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={2}
                placeholder="Any notes about this equipment…"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Update Equipment' : 'Save Equipment'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
      {required && <span className="ml-1 text-mozaic-red">*</span>}
    </Label>
  );
}
